import { useCallback, useEffect, useRef, memo } from 'react';

import { useAppConfig } from '@Contexts/AppConfig';

import type { FC, HTMLAttributes } from 'react';
import type { YandexBadgeOptions, YandexSplitOptions } from '@Types/YandexSplitWidget';

export interface YandexSplitBadgeProps extends HTMLAttributes<HTMLDivElement> {
  amount: string;
  align?: YandexBadgeOptions['align'];
  type?: YandexBadgeOptions['type'];
  variant?: YandexBadgeOptions['variant'];
  theme?: YandexBadgeOptions['theme'];
  color?: YandexBadgeOptions['color'];
  size?: YandexBadgeOptions['size'];
}

const YandexSplitBadge: FC<YandexSplitBadgeProps> = (props) => {
  const {
    type = 'cashback',
    variant = 'default',
    theme = 'light',
    color = 'transparent',
    align = 'left',
    amount,
    className,
    size = 's',
    ...restProps
  } = props;
  const { yandexMerchantId: merchantId } = useAppConfig();
  const mountedRef = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleError = () => {
    // eslint-disable-next-line
    console.error('Error while mounting yandex badge');
  };

  const onYaPayLoad = useCallback(() => {
    const YaPayInstance: YandexSplitOptions = window.YaPay;

    if (!containerRef.current) return;
    if (!merchantId) return;

    if (mountedRef.current) return;
    mountedRef.current = true;

    if (YaPayInstance) {
      try {
        YaPayInstance.mountBadge(containerRef.current, {
          type,
          amount,
          size,
          theme,
          align,
          color,
          variant,
          merchantId,
        });
      } catch {
        handleError();
      }
    }
  }, [merchantId, type, amount, size, theme, color, variant, align]);

  useEffect(() => {
    const container = containerRef.current;
    const script = document.createElement('script');

    script.src = 'https://pay.yandex.ru/sdk/v1/pay.js';
    script.async = true;
    script.onload = onYaPayLoad;

    document.body.appendChild(script);
    return () => {
      mountedRef.current = false;
      document.body.removeChild(script);
      container?.firstChild?.remove();
    };
  }, [onYaPayLoad]);

  return <div {...restProps} ref={containerRef} className={className}></div>;
};

export default memo(YandexSplitBadge);
